import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { LANGUAGES, SIMULATION_STATUS } from './simulation'
import type { ReportReason } from './community'
import type { AdminCostUserView } from './confirmed-events'
import { BROADCAST_PURPOSES } from './marketing-consent'
import { inAppPathSchema } from './notifications'
import { QUOTE_STATUSES, type QuoteResult, type QuoteView } from './quotes'
import type { QuoteStatusActor } from '../quote-status'

/** 견적 요청 SLA — 이 일수 안에 공장 견적을 넣어야 한다. 넘으면 어드민에 "지연" 표시 + 배지 */
export const QUOTE_SLA_DAYS = 3

/** GET /admin/summary — 사이드바 배지·대시보드 상단 */
export interface AdminSummary {
  quotes: {
    /** 공장 견적을 아직 안 넣은 요청 */
    submitted: number
    /** 사용자가 공장을 골라서 후속 연결이 필요한 요청 */
    accepted: number
    /** submitted 중 SLA(3일)를 넘긴 요청 */
    overdue: number
  }
  /** 로컬 E2E 우회로 들어온 세션인지 (화면에 표시) */
  e2e: boolean
}

/** 어드민용 견적 뷰 — 사용자 뷰 + 운영 필드 (사용자에게는 안 내려간다) */
export interface AdminQuoteView extends QuoteView {
  adminNotes: string
  acceptedAt: string | null
  closedAt: string | null
  /** 제출 후 지난 일수 (소수 1자리) */
  ageDays: number
  /** submitted 인데 SLA(3일)를 넘김 */
  overdue: boolean
  /** 견적에 쓴 견적권의 결제 출처 (C32). 견적권 행이 없으면 null, 옛 서버면 없다 */
  paymentSource?: QuotePaymentSource | null
}

/**
 * 견적 결제 출처 — 견적권 행의 source(purchase·included·admin)에 is_sandbox 를 얹은 4종.
 * TestFlight·심사·라이선스 테스터 결제는 source 와 상관없이 sandbox 로 묶어 실결제와 가른다
 */
export const QUOTE_PAYMENT_SOURCES = ['purchase', 'included', 'admin', 'sandbox'] as const
export type QuotePaymentSource = (typeof QUOTE_PAYMENT_SOURCES)[number]

export function quotePaymentSource(
  credit: { source: Exclude<QuotePaymentSource, 'sandbox'>; isSandbox: boolean } | undefined,
): QuotePaymentSource | null {
  if (!credit) return null
  return credit.isSandbox ? 'sandbox' : credit.source
}

/** 관리자 견적 목록의 출처 거르기. 'all' 은 출처 없는 요청까지 모두 */
export function quoteMatchesPaymentSource(
  quote: Pick<AdminQuoteView, 'paymentSource'>,
  filter: QuotePaymentSource | 'all',
): boolean {
  return filter === 'all' || quote.paymentSource === filter
}

/** 공장 견적 내역의 종류 — 입력 · 수정 · 삭제 */
export const QUOTE_OFFER_EVENT_KINDS = ['added', 'updated', 'removed'] as const
export type QuoteOfferEventKind = (typeof QUOTE_OFFER_EVENT_KINDS)[number]

/** 공장 견적 내역 1건 — 누가 언제 어떤 값으로 입력·수정·삭제했는지. 견적을 지워도 내역은 남는다 */
export interface QuoteOfferEventView {
  id: string
  offerId: string
  kind: QuoteOfferEventKind
  /** 그 시점의 견적 값 (added·updated = 저장된 값, removed = 지우기 직전 값) */
  offer: QuoteResult
  adminEmail: string
  createdAt: string
}

/** 견적 상태 변경 이력 1건 (quote_status_events). 요청 상태는 scope 'request', 제안 상태(고객 선택)는 'offer' */
export interface QuoteStatusEventView {
  id: string
  scope: 'request' | 'offer'
  offerId: string | null
  /** 바뀌기 전 상태. 모르면 null (계정 삭제의 일괄 마감, 고객이 고른 제안) */
  from: string | null
  to: string
  actor: QuoteStatusActor
  reason: string
  createdAt: string
}

/** GET /admin/quotes/:id 및 견적 입력·수정·삭제 응답 — 요청 + 견적 내역(최신순) */
export interface AdminQuoteDetail {
  quote: AdminQuoteView
  history: QuoteOfferEventView[]
  /** 상태 변경 이력 (최신순). 이 칸이 생기기 전 서버에는 없다 */
  statusHistory?: QuoteStatusEventView[]
}

export const adminQuoteListQuerySchema = z.object({
  status: z.enum(QUOTE_STATUSES).optional(),
  /** 1 이면 SLA 초과만 */
  overdue: z.coerce.boolean().optional(),
  /** 제품명 · 연락처 이름 · 이메일 부분 일치 */
  q: z.string().trim().max(80).optional(),
})
export type AdminQuoteListQuery = z.infer<typeof adminQuoteListQuerySchema>

export const adminQuoteNotesSchema = z.object({ adminNotes: z.string().max(4000) })

/** 사용자에게 인앱 알림 + 푸시 */
export const adminMessageSchema = z.object({
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(500),
  /** 앱 화면 경로 (expo-router href, '/' 로 시작). 비우면 알림 탭. 외부 주소는 받지 않는다 (2026-10-08) */
  url: inAppPathSchema.optional(),
})
export type AdminMessage = z.infer<typeof adminMessageSchema>

export const adminBroadcastSchema = adminMessageSchema.extend({
  /** 확인용 — 받는 사람 수를 미리 보고 그대로 보내야 한다 */
  expectedRecipients: z.number().int().min(1),
  /**
   * 공지 성격 (로드맵 P6). advertising = 혜택·새 기능 홍보 — 광고성 수신에 동의한 사람에게만 '(광고)' 표기를 붙여
   * 낮(08~21시)에만 푸시한다. notice = 약관 변경·장애처럼 광고 없는 안내 — 모두에게. 칸이 없으면(옛 관리자) 광고성
   */
  purpose: z.enum(BROADCAST_PURPOSES).default('advertising'),
})

export const adminCreditGrantSchema = z.object({
  kind: z.enum(['quote', 'generations']),
  amount: z.number().int().min(1).max(100),
  reason: z.string().trim().min(1).max(200),
})
export type AdminCreditGrant = z.infer<typeof adminCreditGrantSchema>

export const adminSimulationListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(10),
  status: z.enum(SIMULATION_STATUS).optional(),
  /** passed = 게이트 통과 · bestof = 3번 다 실패해 최선을 채택 */
  gate: z.enum(['passed', 'bestof']).optional(),
  categoryId: z.enum(PRODUCT_CATEGORY_IDS).optional(),
  language: z.enum(LANGUAGES).optional(),
  userId: z.string().trim().max(80).optional(),
  /** 이 코드 검사에서 실패한 것만 (예: margin-positive) */
  check: z.string().trim().max(40).optional(),
  /** 제품명 부분 일치 */
  q: z.string().trim().max(80).optional(),
})
export type AdminSimulationListQuery = z.infer<typeof adminSimulationListQuerySchema>

/** POST /admin/simulations/translate-missing 한 번에 띄우는 제품 수 — 아무것도 안 주면 이만큼만 (2026-10-16) */
export const TRANSLATE_MISSING_DEFAULT_LIMIT = 20
/** 같은 요청의 절대 상한 (제품 200건 × 언어 최대 3개 = 번역 600건). 넘으면 잘라내지 않고 400 으로 거절한다 */
export const TRANSLATE_MISSING_MAX_LIMIT = 200

/**
 * POST /admin/simulations/translate-missing 의 쿼리·본문 (같은 스키마로 각각 검사하고, 둘 다 주면 쿼리가 이긴다).
 * lang 이 없으면 원문 언어를 뺀 나머지 언어 중 빈 것 전부, 있으면 그 언어만. limit 이 없으면 TRANSLATE_MISSING_DEFAULT_LIMIT
 * (기본값을 스키마에 넣으면 쿼리 쪽 기본값이 본문 값을 덮으므로 라우트에서 채운다).
 * 모르는 키(예: language 오타)는 400 — 조용히 무시하고 전 언어로 돌지 않게
 */
export const adminTranslateMissingSchema = z.strictObject({
  lang: z.enum(LANGUAGES).optional(),
  limit: z.coerce.number().int().min(1).max(TRANSLATE_MISSING_MAX_LIMIT).optional(),
})
export type AdminTranslateMissingInput = z.infer<typeof adminTranslateMissingSchema>

export const adminStatsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(30),
})

/**
 * 생성 실패 분류 코드 (2026-10-17) — simulations.internal.failure.code · internal.photoFailure.code.
 * 서버가 오류 종류·문구로 고른다(apps/api/src/lib/failure-code.ts). 문구 원문은 운영 알림·Sentry 로 보내지 않고 이 코드만 보낸다
 */
export const SIM_FAILURE_CODES = [
  'timeout',
  'model_error',
  'rate_limit',
  'credit',
  'no_result',
  'db',
  'unknown',
] as const
export type SimFailureCode = (typeof SIM_FAILURE_CODES)[number]

/** 관리자 화면·운영 알림의 분류 코드 이름 */
export const SIM_FAILURE_CODE_LABELS: Record<SimFailureCode, string> = {
  timeout: '시간 초과',
  model_error: 'AI 모델 오류',
  rate_limit: '요청 한도(429)',
  credit: 'AI 크레딧 부족(402)',
  no_result: '결과 형식 불량',
  db: 'DB 오류',
  unknown: '분류 안 됨',
}

/**
 * 생성이 실패한 단계 (internal.failure.stage). 생성 워크플로의 단계 묶음 이름이다 — 워크플로 단계 이름(core-1 …)과 다르다.
 * detail = 시세 조회 ∥ 상세 → 심사(같이 돈다), photos = 사진 ∥ 비교 제품, notify = 완성 알림(인앱·푸시).
 * 워크플로가 단계를 넘기지 않은 옛 실행은 마지막 진행 단계(internal.progress.stage)를 쓴다.
 * 사진 실패(internal.photoFailure.stage)는 'photo-plan' · 'photo-<컷 종류>' 다
 */
export const SIM_FAILURE_STAGES = [
  'prepare',
  'core',
  'detail',
  'gate',
  'draft-save',
  'save',
  'photos',
  'notify',
  'unknown',
] as const
export type SimFailureStage = (typeof SIM_FAILURE_STAGES)[number]

/** internal.failure · internal.photoFailure 한 건. at 은 ISO 시각 */
export interface SimFailureRecord {
  stage: string
  code: SimFailureCode
  at: string
}

/**
 * GET /admin/stats 의 실패 분석 (2026-10-17, 오류 수집 점검 46/02 의 S5). 선택 필드 — 이 필드 전 서버는 보내지 않는다.
 * 복사본(internal.duplicatedFrom)은 생성이 아니라서 뺀다.
 */
export interface AdminFailureStats {
  /** 이번 기간 실패한 생성의 단계·분류 코드별 건수 (많은 순). 기록을 남기기 전(이 배포 전) 실패는 stage·code 가 '' */
  byReason: { stage: string; code: string; n: number }[]
  /**
   * 이번 기간 생성의 플랫폼·앱 버전별 생성·실패 수 (많은 순, 최대 30줄). 앱이 보낸 X-App-* 헤더를 생성 때 저장한 값(internal.client).
   * platform: ios · android · web · other · legacy(버전 헤더 없는 옛 앱 1.0.0~1.0.2) · admin(관리자 재실행) · unknown(기록 전)
   */
  byClient: { platform: string; version: string | null; generations: number; failed: number }[]
  /** 지금 진행 중(running·queued)인데 minutes 분 넘게 갱신이 없는 생성 수 (기간과 무관한 지금 상태) */
  stuck: { minutes: number; running: number; queued: number }
  /** 이번 기간 사진 단계가 끝난 생성(total) 중 사진이 한 장도 없는 건(failed), 일부 컷만 실패한 건(partial) */
  photos: { total: number; failed: number; partial: number }
}

/** GET /admin/stats — 대시보드 */
export interface AdminStats {
  days: number
  /** 일별 (오래된 날 → 오늘) */
  daily: {
    date: string
    generations: number
    failed: number
    bestOf: number
    usd: number
  }[]
  totals: {
    generations: number
    failed: number
    bestOf: number
    avgAttempts: number
    /** 이번 기간 AI 비용 합 (usd 를 아는 호출만) */
    usd: number
    /** 비용을 아는 호출 수 / 전체 호출 수 */
    costedCalls: number
    totalCalls: number
    images: number
    searches: number
    /** 제품 1개당 평균 비용 (비용을 아는 생성만) */
    usdPerGeneration: number | null
  }
  /** 실결제 구독 수. 샌드박스(테스터) 구독은 빼고 sandboxPlans 에 따로 센다 (1.0.6 점검 P2-11 부터) */
  plans: { free: number; basic: number; pro: number; trial: number }
  /** TestFlight·심사·라이선스 테스터 구독 수 (user_plans.is_sandbox). 선택 필드 — 옛 서버는 보내지 않는다 */
  sandboxPlans?: { basic: number; pro: number; trial: number }
  /** 이번 기간 한도 80% 이상 쓴 유료 사용자. isSandbox 는 테스터 구독 (선택 필드 — 옛 서버는 보내지 않는다) */
  nearLimit: { userId: string; plan: string; used: number; limit: number; isSandbox?: boolean }[]
  quotes: { submitted: number; quoted: number; accepted: number; closed: number; overdue: number }
  /**
   * 기능·모델별 AI 비용 (generation_costs.purpose, model 로 묶음, 비용 큰 순, 최대 60개). 선택 필드 — 옛 서버는 보내지 않는다.
   * 이름은 '바깥/안' (예: 'edit/language-fix', 'price-seed/price-lookup'), 기록 전 호출은 ''.
   * 같은 purpose 에 모델이 둘이면 가벼운 모델(Haiku)이 실패해 Sonnet 으로 대체(폴백)된 호출이 있다는 뜻 (2026-10-16).
   * usd 는 소수 넷째 자리까지 (Haiku 호출은 건당 $0.001 아래라 셋째 자리에서 0 으로 보였다).
   * n 은 usd 를 모르는(null) 호출까지 센 전체 호출 수, costed 는 그중 usd 를 아는 호출 수 (2026-10-16, 선택 필드 —
   * 이 필드 전 서버는 보내지 않는다). 호출당 비용은 usd / costed, n - costed 가 '비용 미확인' 건수
   */
  purposes?: { purpose: string; model: string; n: number; costed?: number; usd: number }[]
  /** 실패 분석 — 단계·사유별, 플랫폼·버전별, 멈춘 생성, 사진 실패 (2026-10-17, 선택 필드) */
  failures?: AdminFailureStats
  /** 이번 기간 AI 비용이 큰 사용자 (최대 20명, 로그인 사용자만). 선택 필드 — 옛 서버는 보내지 않는다 */
  topCostUsers?: AdminCostUserView[]
}

export interface AdminAuditView {
  id: string
  adminEmail: string
  action: string
  targetType: string | null
  targetId: string | null
  detail: Record<string, unknown> | null
  createdAt: string
}

export interface GoldenCoverageRow {
  categoryId: string
  market: string
  count: number
  priced: number
  /** priced < 3 이면 생성 시 가격 범위를 못 준다 */
  ok: boolean
}

export interface PriceCacheEntry {
  name: string
  unit: string
  retailKrw: number | null
  wholesaleKrw: number | null
  avgKrw: number | null
}

/** 사용자 제품 목록 (어드민 조회) */
export interface AdminUserSimulation {
  id: string
  productName: string | null
  categoryId: string
  status: string
  isDeleted: boolean
  createdAt: string
  gatePassed: boolean | null
}

/** 약관의 '24시간 안 검토' — 신고 후 이만큼 지나도 처리 표시가 없으면 서버가 경보하고 어드민 대기열은 빨간 배지 */
export const REPORT_REVIEW_HOURS = 24

/** 신고 대상 종류 라벨 (어드민·운영 경보는 한국어). admin_audit.target_type 값 */
export const REPORT_TARGET_LABELS: Record<string, string> = {
  post: '커뮤니티 글',
  comment: '커뮤니티 댓글',
  simulation: 'AI 결과',
}

/** GET /admin/reports 의 신고 1건. id 는 신고 기록(admin_audit)의 id */
export interface QueuedReport {
  id: string
  kind: 'community' | 'ai'
  targetType: string
  targetId: string
  reason: ReportReason | null
  reasonLabel: string
  /** 커뮤니티 신고 메모 */
  note: string | null
  /** AI 결과 신고: 신고한 AI 답 원문 */
  excerpt: string | null
  /** 커뮤니티 신고: 글 id (댓글이면 달린 글) */
  postId: string | null
  /** 신고하면서 작성자를 차단했는지 */
  blocked: boolean
  /** ISO 시각 (어드민의 다른 목록과 같은 모양) */
  createdAt: string
}
